// apps/client/src/ui/screens/chat.tsx (M0-T19, §8.7) — the chat panel: recent log, quick phrases, keyboard line (Enter sends),
// and a pill to the emote wheel. Loading this module also mounts the bubble layer (the screens registry imports every screen).
import { tuning } from '@whiskerton/content';
import { useEffect, useState, type FormEvent } from 'react';
import { useCommands } from '../../game/instance';
import { useGame } from '../../state/store';
import { mountBubbles } from '../chat/Bubbles';
import { ChatLog } from '../chat/ChatLog';
import { QuickPhrases } from '../chat/QuickPhrases';
import { useT } from '../i18n';
import { Button, Panel, TextField } from '../kit';
import type { ScreenDef } from '../router';

declare module '../router' { interface ScreenParams { chat: undefined } }

mountBubbles();

const CSS = `
.chat-panel { display: flex; flex-direction: column; gap: 14px; width: min(560px, 78cqw); }
.chat-log { list-style: none; margin: 0; padding: 12px 14px; display: grid; gap: 6px; max-height: 30cqh; overflow-y: auto;
  background: var(--cream-100); border-radius: 16px; box-shadow: var(--sh-well); font-weight: 700; overflow-wrap: anywhere; }
.chat-name { font-weight: 900; }
.chat-empty { margin: 0; padding: 12px 14px; color: var(--wood-600); font-weight: 800; }
.chat-phrases { display: flex; flex-wrap: wrap; gap: 8px; }
.chat-phrase { border: 0; font: inherit; font-weight: 900; font-size: 15px; cursor: pointer; }
.chat-phrase:focus-visible { outline: 3px solid var(--sky-700); outline-offset: 3px; }
.chat-send { display: grid; grid-template-columns: 1fr auto; gap: 10px; align-items: end; }
.chat-send .field { gap: 0; min-width: 0; }
.chat-send .field input { box-sizing: border-box; width: 100%; min-width: 0; }
.chat-tools { display: flex; justify-content: flex-start; }
/* A landscape phone (§8.7): two columns that fit the screen without scrolling the panel. The log takes the whole left side, newest at
   the bottom; the quick phrases scroll on their own above the keyboard line and the Emotes pill on the right. */
@container (max-height: 560px) {
  .panel.chat-panel { display: grid; width: min(780px, 90cqw); max-width: 90cqw; height: 86cqh; overflow: hidden; padding-top: 26px;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); grid-template-rows: minmax(0, 1fr) auto auto;
    grid-template-areas: 'log phrases' 'log send' 'log tools'; column-gap: 16px; row-gap: 10px; }
  .chat-panel > .chat-log, .chat-panel > .chat-empty { grid-area: log; max-height: none; min-height: 0; align-content: end; }
  .chat-panel > .chat-phrases { grid-area: phrases; min-height: 0; overflow-y: auto; align-content: flex-start; padding: 2px 2px 6px; margin: -2px; }
  .chat-panel > .chat-send { grid-area: send; }
  .chat-panel > .chat-tools { grid-area: tools; }
}
`;
let styled = false;
function injectStyle(): void {
  if (styled || typeof document === 'undefined') return;
  styled = true;
  const el = document.createElement('style');
  el.dataset.owner = 'chat';
  el.textContent = CSS;
  document.head.appendChild(el);
}

/** A mouse or trackpad: focus the keyboard line at once. On a touch screen that would raise the on-screen keyboard over the panel. */
const finePointer = (): boolean => typeof matchMedia === 'function' && matchMedia('(pointer: fine)').matches;

function Chat({ close }: { close: () => void }) {
  injectStyle();
  const t = useT();
  const cmd = useCommands();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    useGame.getState().chatSetOpen(true);
    return () => useGame.getState().chatSetOpen(false);
  }, []);
  const say = async (line: string) => {
    if (busy) return;
    setBusy(true);
    try { if (await cmd.say(line) && line === text) setText(''); } finally { setBusy(false); }
  };
  const submit = (e: FormEvent) => { e.preventDefault(); void say(text); };
  const emotes = () => { close(); useGame.getState().openScreen('emotes', undefined); };
  return (
    <Panel title={t('chat.title')} className="chat-panel">
      <ChatLog />
      <QuickPhrases say={(p) => void say(p)} />
      <form className="chat-send" onSubmit={submit}>
        <TextField label="" value={text} onChange={setText} maxLength={tuning.net.chatMaxChars} placeholder={t('chat.placeholder')} autoFocus={finePointer()} />
        <Button type="submit" disabled={busy || !text.trim()}>{t('chat.send')}</Button>
      </form>
      <div className="chat-tools">
        <button type="button" className="pill chat-phrase" onClick={emotes}>{t('chat.emotes')}</button>
      </div>
    </Panel>
  );
}
export const screen: ScreenDef<'chat'> = { id: 'chat', layer: 'panel', component: Chat };
